import React, {useEffect} from "react";
import BasketItem from "modules/booking/types/BasketItem";
import InputField from "modules/ui/components/InputField/InputField";
import useTranslate from "modules/core/languages/hooks/useTranslate";
import {StarIcon} from "@heroicons/react/24/solid";
import TextAreaField from "../../modules/ui/components/TextAreaField/TextAreaField";
import SelectField from "../../modules/ui/components/SelectField/SelectField";
import moment from "moment";
import useCustomerContext from "../../modules/customers/hooks/useCustomerContext";
import {SalesChannelEnum} from "../../modules/common/enums/SalesChannelEnum";

interface RoomDetailsProps {
    form: any,
    basketItem: BasketItem,
    index: number
    key?: number,
    prebookingFirstName?: string,
    prebookingSurname?: string,
    setValue?: any
}

const RoomDetail: React.FC<RoomDetailsProps> = ({ form, basketItem, index, prebookingFirstName, prebookingSurname, setValue }) => {
    const t = useTranslate();
    const {languageCode} = useCustomerContext();

    const { errors } = form.formState;

    // Set the prebooking name and surname as form values when component mounts
    useEffect(() => {
        if (basketItem.roomHotelEventSalesChannel === SalesChannelEnum.PACKAGE && prebookingFirstName && prebookingSurname && setValue) {
            setValue(`guests[${index}][guestsByRooms][0][name]`, prebookingFirstName);
            setValue(`guests[${index}][guestsByRooms][0][surname]`, prebookingSurname);
        }
    }, [index, basketItem.roomHotelEventSalesChannel, prebookingFirstName, prebookingSurname, setValue]);

    const getGuestByRoomError = (roomIndex: number, guestIndex: number, field: string) => {
        const guest = errors.guests?.at(roomIndex)?.guestsByRooms?.at(guestIndex);

        return guest ? guest[field]?.message : undefined;
    }

    return (
        <div className="bg-gray-100 rounded p-3 mb-4">
            <input type="hidden" {...form.register(`guests[${index}][eventHotelRoom]`)} defaultValue={basketItem.roomHotelEvent} />
            <input type="hidden" {...form.register(`guests[${index}][prebookingRoomId]`)} defaultValue={basketItem.id} />
            <div className="flex items-end">
                {[...Array(basketItem.hotelObject.stars)].map((value, index) => <StarIcon className="w-[18px] h-[18px] text-orange-500" key={index} />)}
            </div>
            <h4 className="text-xl font-semibold mb-1">{basketItem.hotelObject.name}</h4>
            <h5 className="text-xl text-gray-500 font-semibold mb-2">{basketItem.trName[languageCode]}</h5>

            <div className="lg:flex lg:space-x-5">
                <div className="lg:w-1/2 mb-2">
                    <SelectField
                        label={`${t("booking.checkInTime")} (${moment(basketItem.arrivalDate).format('DD/MM/YYYY')})`}
                        defaultValue=""
                        {...form.register(`guests[${index}][arrivalHour]`)}
                        error={errors.guests?.at(index)?.arrivalHour?.message}
                    >
                        <option value="">{t("booking.iDontKnow")}</option>
                        <option value="00:00">00:00</option>
                        <option value="01:00">01:00</option>
                        <option value="02:00">02:00</option>
                        <option value="03:00">03:00</option>
                        <option value="11:00">11:00</option>
                        <option value="12:00">12:00</option>
                        <option value="13:00">13:00</option>
                        <option value="14:00">14:00</option>
                        <option value="15:00">15:00</option>
                        <option value="16:00">16:00</option>
                        <option value="17:00">17:00</option>
                        <option value="18:00">18:00</option>
                        <option value="19:00">19:00</option>
                        <option value="20:00">20:00</option>
                        <option value="21:00">21:00</option>
                        <option value="22:00">22:00</option>
                        <option value="23:00">23:00</option>
                    </SelectField>
                </div>
            </div>

            {[...Array(basketItem.occupancy)].map((v, guestIndex) => (
                <React.Fragment key={guestIndex}>
                    <h6 className="text-lg font-semibold mb-1">{t("booking.guestN", { number: guestIndex + 1 })}{guestIndex >= basketItem.occupancy - basketItem.children ? ` (${t("booking.child")})` : null}</h6>
                    <div className="lg:flex lg:space-x-5">
                        <div className="lg:w-1/2 mb-2">
                            <InputField
                                label={t("booking.name")}
                                type="text"
                                className={`${basketItem.roomHotelEventSalesChannel === SalesChannelEnum.PACKAGE && prebookingFirstName && guestIndex === 0 ? 'opacity-60 cursor-not-allowed' : ''}`}
                                disabled={basketItem.roomHotelEventSalesChannel === SalesChannelEnum.PACKAGE && prebookingFirstName && guestIndex === 0}
                                {...form.register(`guests[${index}][guestsByRooms][${guestIndex}][name]`, { required: t("common.feedback.field_required") })}
                                error={getGuestByRoomError(index, guestIndex, 'name')}
                            />
                        </div>
                        <div className="lg:w-1/2 mb-2">
                            <InputField
                                label={t("booking.surname")}
                                type="text"
                                className={`${basketItem.roomHotelEventSalesChannel === SalesChannelEnum.PACKAGE && prebookingSurname && guestIndex === 0? 'opacity-60 cursor-not-allowed' : ''}`}
                                disabled={basketItem.roomHotelEventSalesChannel === SalesChannelEnum.PACKAGE && prebookingSurname && guestIndex === 0}
                                {...form.register(`guests[${index}][guestsByRooms][${guestIndex}][surname]`, { required: t("common.feedback.field_required") })}
                                error={getGuestByRoomError(index, guestIndex, 'surname')}
                            />
                        </div>
                    </div>
                </React.Fragment>)
            )}

            <TextAreaField
                label={t("booking.comments")}
                defaultValue=""
                {...form.register(`guests[${index}][comments]`)}
                error={errors.guests?.at(index)?.comments?.message}
            />
        </div>
    );
}

export default RoomDetail;
